import $ from 'jquery';
import 'jquery-validation';
import BaseFeature from '../../foundation/base/base';
import { formIdparser } from '../../foundation/utilities/formIdParser';

/**
 * This is a custom form handler that validates fields directly on user input in sitecore forms
 * It's a little bit 'hacky' due to how restricted we are in working with sitecore forms
 * as well as time-effort restrictions from Perstorp, and should be optimized if we get the time.
 *
 * It requires a specific form structure with correct use of classes by editors.
 *
 * - Every form step needs to be wrapped in a section with the class 'js-form-section'.
 * - Every input field within a form step needs to be wrapped in its own section, no class needed.
 * - Every input field needs to have the class 'js-validate-on-blur'.
 * - Every form navigation button needs to have one of the following classes:
 *   'js-next-section', 'js-previous-section'
 *
 * As of now, the validation supports regular inputs, selects, and textareas.
 * Checkboxes and radiobuttons have not been fully tested.
 *
 * Since the validation requires a mixture of client-side handling and the Sitecore forms session,
 * the solution can't be fully tested within the design system.
 *
 * - Erik Lundow 2024-07-25
 */

export default class FormHandler extends BaseFeature {
	constructor(element) {
		super(element);
		this.setRequiredFields = this.setRequiredFields.bind(this);
		this.setNextButtons = this.setNextButtons.bind(this);
		this.setPreviousButtons = this.setPreviousButtons.bind(this);
		this.initDomCache();
		this.el = element;
		this.formSections = this.el.querySelectorAll('.js-form-section');
		this.nextButtons = this.el.querySelectorAll('.js-next-section');
		this.previousButton = this.el.querySelectorAll('.js-previous-section');
		this.fieldsWithDynamicValidation = this.el.querySelectorAll('.js-validate-on-blur');
		this.dropdownContainers = this.el.querySelectorAll('.js-droplist-component');
		this.currentSection = 0;
		this.oldSecondValue = '';
		this.hideSections();
		this.initEvents();
		this.initDropdownEvents();
		this.simulateDropdownChange();
		this.storeOriginalDropdownValues();
	}

	simulateDropdownChange() {
		const dropdown = document.querySelector('#subject-dropdown-id');
		if (dropdown) {
			dropdown.dispatchEvent(new Event('change', { bubbles: true }));
		}
	}


	/**
     * Hides all form sections except the first one.
     */
	hideSections() {
		if (this.formSections && this.formSections.length) {
			this.formSections.forEach((section, index) => {
				section.classList.toggle('u-hide', index !== 0);
			});
		}
	}

	/**
     * Initializes event listeners for form navigation and validation.
     */
	initEvents() {
		if (this.formSections && this.formSections.length) {
			this.setRequiredFields();
		}

		if (this.fieldsWithDynamicValidation && this.fieldsWithDynamicValidation.length) {
			this.setValidationListener();
			this.setFocusListener();
		}
		if (this.nextButtons && this.nextButtons.length) {
			this.setNextButtons();
		}

		if (this.previousButton && this.previousButton.length) {
			this.setPreviousButtons();
		}
	}

	/**
     * Initializes dropdown-related events for dependent dropdowns.
     */
	initDropdownEvents() {
		this.dropdownContainers.forEach((container) => {
			const firstDropdown = container.querySelector('.js-first-dropdown');
			const secondDropdown = container.querySelector('.js-second-dropdown');
			const thirdDropdownContainer = container.querySelector('.js-third-dropdown-checkboxes');
			const hiddenInput = container.querySelector('input[type="hidden"]');
			const listSeparator = container.getAttribute('data-list-separator') || '__';
			const multilistValueSeparator = container.getAttribute('data-multilist-value-separator') || '-';
			const contextItemId = container.getAttribute('data-context-item-id');

			if (!firstDropdown || !secondDropdown || !thirdDropdownContainer || !hiddenInput || !contextItemId) {
				console.error('❌ Required elements not found in container:', { container });
				return;
			}

			secondDropdown.addEventListener('focus', () => {
				this.oldSecondValue = secondDropdown.value;
			});

			secondDropdown.addEventListener('mousedown', () => {
				this.oldSecondValue = secondDropdown.value;
			});

			secondDropdown.addEventListener('change', () => {
				const newSecondValue = secondDropdown.value;
				this.updateFirstDropdownValueChangeSecond(firstDropdown, newSecondValue, listSeparator, multilistValueSeparator);
				this.syncHiddenInput(firstDropdown.value, hiddenInput);
				this.loadThirdDropdown(contextItemId, newSecondValue, thirdDropdownContainer, container, listSeparator, multilistValueSeparator);
			});

			firstDropdown.addEventListener('change', (e) => {
				const baseValue = e.target.value.split(listSeparator)[0];

				this.loadSecondDropdown(contextItemId, baseValue, secondDropdown, listSeparator, () => {
					const newSecondValue = secondDropdown.value;
					this.updateFirstDropdownValue(firstDropdown, newSecondValue, thirdDropdownContainer, listSeparator);
					this.loadThirdDropdown(contextItemId, newSecondValue, thirdDropdownContainer, container, listSeparator, multilistValueSeparator);
				});
			});

			this.loadSecondDropdown(contextItemId, firstDropdown.value.split(listSeparator)[0], secondDropdown, listSeparator, () => {
				const newSecondValue = secondDropdown.value;
				this.loadThirdDropdown(contextItemId, newSecondValue, thirdDropdownContainer, container, listSeparator, multilistValueSeparator);

				this.updateFirstDropdownValue(firstDropdown, newSecondValue, thirdDropdownContainer, listSeparator);
				this.syncHiddenInput(firstDropdown.value, hiddenInput);
			});
		});
	}

	removeMultilistOptions(firstDropdownValue) {
		const separator = "_MultilistValueSeparator_";
		const index = firstDropdownValue.indexOf(separator);
		const result = index !== -1 ? firstDropdownValue.substring(0, index) : firstDropdownValue;
		return result;
	}


	/**
     * Updates the options in the second dropdown based on fetched data.
     */
	updateSecondDropdown(secondDropdown, data) {
		secondDropdown.innerHTML = '';
		let selected = '';
		data.items.forEach((item) => {
			const option = document.createElement('option');
			option.value = item.Id;
			option.textContent = item.DisplayName;
			option.selected = item.IsSelected;
			secondDropdown.appendChild(option);

			if (item.IsSelected) { selected = item.Id; }
		});
		secondDropdown.disabled = data.items.length === 0;

		return selected;
	}

	/**
     * Fetches options for the second dropdown based on the selected value in the first dropdown.
     */
	loadSecondDropdown(contextItemId, parentId, secondDropdown, listSeparator, callback) {
		const templateId = secondDropdown.getAttribute('data-template-id');
		const pageItemId = secondDropdown.getAttribute('data-page-item-id');
		const firstDropdown = document.querySelector('.js-first-dropdown');

		if (!parentId || !templateId || !pageItemId || !contextItemId) {
			console.warn('Missing required attributes for loading second dropdown:', {
				parentId, templateId, pageItemId, contextItemId
			});

			if (typeof callback === 'function') callback();
			return;
		}

		fetch('/api/Forms/GetSecondDropdownItems', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				parentId, templateId2: templateId, currentPageItemId: pageItemId, contextItemId
			})
		})
			.then(response => response.json())
			.then((data) => {
				if (!data || !data.items) {
					console.error('API returned invalid data:', data);
					return;
				}

				const secondSelected = this.updateSecondDropdown(secondDropdown, data);

				if (firstDropdown) {
					const firstValue = firstDropdown.value.split(listSeparator)[0] || '';
					const secondValue = secondSelected || '';
					const newValue = `${firstValue}${listSeparator}${secondValue}`;

					const selectedOption = firstDropdown.querySelector('option:checked') || firstDropdown.options[0];

					this._updateSelectedOption(firstDropdown, selectedOption, newValue);
				}

				if (typeof callback === 'function') {
					callback();
				} else {
					console.warn('❗ Callback is not a function:', callback);
				}
			})
			.catch((error) => {
				console.error('Error loading second dropdown:', error);

				if (typeof callback === 'function') {
					callback();
				}
			});
	}


	loadThirdDropdown(contextItemId, parentId, thirdDropdownContainer, container, listSeparator, multilistValueSeparator) {
		let templateId = thirdDropdownContainer.getAttribute('data-template-id');
		const pageItemId = thirdDropdownContainer.getAttribute('data-page-item-id');

		if (!templateId) {
			console.warn('templateId missing on thirdDropdownContainer', thirdDropdownContainer);
		}
		if (!pageItemId) {
			console.warn('pageItemId missing on thirdDropdownContainer', thirdDropdownContainer);
		}

		if (!templateId && this.thirdDropdownTemplateId) {
			templateId = this.thirdDropdownTemplateId;
		} else if (templateId) {
			this.thirdDropdownTemplateId = templateId;
		}

		if (!parentId || !contextItemId) {
			console.warn('Missing required attributes for loading third dropdown:', { parentId, contextItemId });
			return;
		}

		fetch('/api/Forms/GetThirdDropdownItems', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				referringItemId: parentId, templateId3: templateId, currentPageItemId: pageItemId, contextItemId
			})
		})
			.then(response => response.json())
			.then((data) => {
				if (!data || !data.items) {
					console.error('API returned invalid data:', data);
					return;
				}
				this.updateThirdDropdown(thirdDropdownContainer, data, container, listSeparator, multilistValueSeparator);
			})
			.catch((error) => {
				console.error('Error loading third dropdown:', error);
			});
	}


	updateThirdDropdown(thirdDropdownContainer, data, container, listSeparator, multilistValueSeparator) {
		thirdDropdownContainer.innerHTML = '';

		if (!data.items.length) {
			return;
		}

		thirdDropdownContainer.style.display = 'grid';
		thirdDropdownContainer.style.gridTemplateColumns = 'repeat(2, 1fr)';
		thirdDropdownContainer.style.gap = '8px 16px';

		data.items.forEach((item) => {
			const checkboxWrapper = document.createElement('div');
			checkboxWrapper.classList.add('form-check');

			checkboxWrapper.style.display = 'flex';
			checkboxWrapper.style.alignItems = 'baseline';
			checkboxWrapper.style.gap = '8px';
			checkboxWrapper.style.width = '100%';
			checkboxWrapper.style.margin = '0';

			const checkbox = document.createElement('input');
			checkbox.type = 'checkbox';
			checkbox.name = 'checkbox-input';
			checkbox.value = item.Id;
			checkbox.id = `checkbox_${item.Id}`;
			checkbox.classList.add('js-third-checkbox', 'form-check-input');

			const label = document.createElement('label');
			label.htmlFor = checkbox.id;
			label.textContent = item.DisplayName;
			label.classList.add('form-check-label');

			label.style.display = 'inline';
			label.style.textAlign = 'left';

			checkboxWrapper.appendChild(checkbox);
			checkboxWrapper.appendChild(label);
			thirdDropdownContainer.appendChild(checkboxWrapper);

			if (item.IsSelected) {
				checkbox.checked = true;
			}

			checkbox.addEventListener('change', () => {
				const firstDropdown = container.querySelector('.js-first-dropdown');
				if (!firstDropdown) {
					console.error('firstDropdown är null vid checkboxändring');
					return;
				}
				this.updateFirstDropdownWithMultiSelect(firstDropdown, thirdDropdownContainer, listSeparator, multilistValueSeparator);
				this.syncHiddenInput(firstDropdown.value, container.querySelector('input[type="hidden"]'));
			});
		});
	}


	updateFirstDropdownValueChangeSecond(firstDropdown, newSecondValue, listSeparator, multilistValueSeparator) {
		const selectedOption = firstDropdown.querySelector('option:checked');

		if (!selectedOption) {
			console.warn('❗ No option marked in firstDropdown');
			return;
		}

		const values = [selectedOption.dataset.originalValue];
		const firstSplitValues = values[0].split(multilistValueSeparator);
		const newArray = firstSplitValues[0].split(listSeparator);
		newArray.pop();
		const newValues = newArray.filter(val => val !== this.oldSecondValue);

		newValues.push(newSecondValue);

		const newValue = newValues.join(listSeparator);

		this._updateSelectedOption(firstDropdown, selectedOption, newValue);
	}


	updateFirstDropdownValue(firstDropdown, newSecondValue, thirdDropdownContainer, listSeparator) {
		const selectedOption = firstDropdown.querySelector('option:checked');

		if (!selectedOption) {
			console.warn('❗ No option marked in firstDropdown');
			return;
		}

		const selectedThirdValues = [...thirdDropdownContainer.querySelectorAll('.js-third-checkbox:checked')]
			.map(checkbox => checkbox.value);


		let values = [selectedOption.dataset.originalValue];

		if (newSecondValue && !selectedOption.dataset.originalValue.includes(newSecondValue)) {
			values.push(newSecondValue);
		}

		values = values.concat(selectedThirdValues);

		const newValue = values.join(listSeparator);

		this._updateSelectedOption(firstDropdown, selectedOption, newValue);
	}

	updateFirstDropdownWithMultiSelect(firstDropdown, thirdDropdownContainer, listSeparator, multilistValueSeparator) {
		const selectedOption = firstDropdown.querySelector('option:checked');

		if (!selectedOption) {
			console.warn('❗ No selected option found in firstDropdown!');
			return;
		}

		const splitValues = selectedOption.value.split(listSeparator);
		const selectedOptionFirstAndSecondDroplist = splitValues.slice(0, 2).join(listSeparator);

		const selectedThirdValues = [...thirdDropdownContainer.querySelectorAll('.js-third-checkbox:checked')]
			.map(checkbox => checkbox.value)
			.join(multilistValueSeparator);

		const newValue = selectedThirdValues
			? `${selectedOptionFirstAndSecondDroplist}${listSeparator}${selectedThirdValues}${multilistValueSeparator}`
			: selectedOptionFirstAndSecondDroplist;

		this._updateSelectedOption(firstDropdown, selectedOption, newValue);
	}

	storeOriginalDropdownValues() {
		this.el.querySelectorAll('.js-first-dropdown option').forEach((option) => {
			option.dataset.originalValue = option.value;
		});
	}

	_updateSelectedOption(firstDropdown, selectedOption, newValue) {
		selectedOption.value = newValue;
		firstDropdown.value = newValue;

		firstDropdown.querySelectorAll('option').forEach((option) => {
			option.selected = option === selectedOption;
		});
	}


	/**
     * Synchronizes the hidden input field with the current dropdown values.
     */
	syncHiddenInput(value, hiddenInput) {
		hiddenInput.value = value;
		hiddenInput.dispatchEvent(new Event('change'));
	}

	/**
     * Sitecore forms handles required fields separately from the input.
     * We need to set the input itself to be required if we want to validate
     * on user input.
     */
	setRequiredFields() {
		this.formSections.forEach((section) => {
			const fields = Array.from(
				section.querySelectorAll('input, select, textarea')
			);
			fields.forEach((field) => {
				if (field.hasAttribute('data-val-required')) {
					field.setAttribute('required', '');
				}
			});
		});
	}

	/**
     * Validates a section.
     * If all fields are valid the current section is hidden and the next set to visible.
     */
	setNextButtons() {
		this.nextButtons.forEach((next) => {
			next.addEventListener('click', (e) => {
				e.preventDefault();

				if (typeof this.currentSection === 'undefined') {
					console.error('this.currentSection is undefined');
					return;
				}

				const isValid = FormHandler.validateSection(this.formSections[this.currentSection]);
				if (!isValid) {
					FormHandler.handleInvalidSection(this.formSections[this.currentSection]);
					return;
				}

				this.currentSection += 1;

				this.formSections.forEach((section, index) => {
					if (index !== this.currentSection) {
						section.classList.add('u-hide');
					} else {
						section.classList.remove('u-hide');
					}
				});
			});
		});
	}


	/**
     * Hides the current section and sets the previous to visible,
     * no validation required.
     */
	setPreviousButtons() {
		this.previousButton.forEach((previous) => {
			this.addEventListener(previous, 'click', (e) => {
				e.preventDefault();
				this.currentSection = this.currentSection - 1;
				this.formSections.forEach((section, index) => {
					if (index !== this.currentSection) {
						section.classList.add('u-hide');
					} else {
						section.classList.remove('u-hide');
					}
				});
			});
		});
	}

	/**
     * Initiates the client-side validation.
     */
	setValidationListener() {
		this.fieldsWithDynamicValidation.forEach((field) => {
			const errorMessage = field.getAttribute('data-val-required');
			field.setAttribute('data-msg-required', errorMessage);

			this.addEventListener(field, 'blur', (e) => {
				FormHandler.validate(e.target);
			});
		});
	}

	/**
     * Validation is a mixture of client-side and Sitecore forms.
     * We need to reset all errors on input focus.
     */
	setFocusListener() {
		this.fieldsWithDynamicValidation.forEach((field) => {
			const errorMessage = field.getAttribute('data-val-required');
			field.setAttribute('data-msg-required', errorMessage);
			this.addEventListener(field, 'focus', (e) => {
				e.target.classList.remove('input-validation-error', 'error');
				FormHandler.removeErrorField(e.target);
				const parent = e.target.parentElement;
				const parsedFormId = formIdparser(e.target.id);
				const fieldError = parent.querySelector(
					`[data-valmsg-for="${parsedFormId}"]`
				);
				if (
					fieldError &&
                    fieldError.classList.contains('field-validation-error')
				) {
					fieldError.innerHTML = '';
					fieldError.classList.add('field-validation-valid');
					fieldError.classList.remove('field-validation-error');
				}
			});
		});
	}

	/**
     * Validates a section on the client side.
     * If errors are identified through Sitecore forms,
     * client validation is skipped, and the section is returned as invalid.
     */
	static validateSection(section) {
		const invalidFields = Array.from(
			section.querySelectorAll('.field-validation-error, .error')
		);
		let hasInvalidFields = false;
		invalidFields.forEach((field) => {
			const parent = field.parentElement;
			const input = parent.querySelector('.js-validate-on-blur');
			if (input.disabled) {
				hasInvalidFields = false;
			} else if (!field.classList.contains('u-hide')) {
				hasInvalidFields = true;
			}
		});

		if (hasInvalidFields) return false;
		const fields = Array.from(
			section.querySelectorAll('input, select, textarea')
		).filter(field => field.type !== 'hidden');
		let isValid = true;
		fields.forEach((field) => {
			if (!$(field).valid()) {
				isValid = false;
			} else {
				field.classList.remove('input-validation-error', 'error');
				FormHandler.removeErrorField(field);
			}
		});

		const droplists = section.querySelectorAll('.js-droplist-component');
		droplists.forEach((droplist) => {
			const droplistIsHidden = window.getComputedStyle(droplist.parentElement).display === 'none';
			if (!droplistIsHidden) {
				const firstDropdown = droplist.querySelector('.js-first-dropdown');

				const rawValue = firstDropdown.nextElementSibling.value || '';
				const multilistEntries = rawValue.split('_MultilistValueSeparator_');

				const parts = multilistEntries.map(entry => entry.split('_ListSeparator_')).flat();

				const checkboxValues = [];
				const checkboxes = droplist.querySelectorAll('.js-third-checkbox');

				checkboxes.forEach((field) => {
					checkboxValues.push(field.value);
				});

				checkboxes.forEach((checkbox) => {
					checkbox.addEventListener('change', () => {
						const existingError = droplist.parentElement.querySelector('.js-error-message-checkbox');
						if (existingError) existingError.remove();
					});
				});

				const hasMatch = checkboxValues.some(value => parts.includes(value));

				if (!hasMatch && checkboxValues.length) {
					isValid = false;

					const existingError = droplist.parentElement.querySelector('.js-error-message-checkbox');
					if (!existingError) {
						const errorSpan = document.createElement('span');
						errorSpan.className = 'js-error-message-checkbox';
						errorSpan.style.color = '#BE3004';
						errorSpan.style.display = 'block';
						errorSpan.style.marginTop = '5px';
						errorSpan.style.fontSize = '14px';
						errorSpan.textContent = 'At least one is required';

						droplist.parentElement.appendChild(errorSpan);
					}
				}
			}
		});
		return isValid;
	}

	/**
     * Handles invalid sections by showing the correct error messages.
     */
	static handleInvalidSection(section) {
		const errors = Array.from(section.querySelectorAll('.error'));
		errors.forEach((error) => {
			if (error.tagName === 'LABEL') {
				const inlineError = error;
				if (!inlineError.classList.contains('u-hide')) {
					inlineError.classList.add('u-hide');
				}
				const parent = error.parentElement;
				const errorInputId = error.getAttribute('for');
				const errorInput = parent.querySelector(`#${errorInputId}`);
				const errorMessage = errorInput.getAttribute('data-val-required');
				const parsedFormId = formIdparser(errorInputId);
				const errorField = parent.querySelector(
					`[data-valmsg-for="${parsedFormId}"]`
				);
				FormHandler.setErrorField(errorField, errorMessage);
			}
		});
	}

	/**
     * Handles invalid fields by setting error messages.
     */
	static handleInvalidField(field) {
		const parent = field.parentElement;
		const errorId = field.id;
		const error = parent.querySelector(`[id="${errorId}-error"]`);
		if (!error.classList.contains('u-hide')) {
			error.classList.add('u-hide');
		}
		const errorMessage = field.getAttribute('data-val-required');

		const parsedFormId = formIdparser(errorId);
		const errorField = parent.querySelector(
			`[data-valmsg-for="${parsedFormId}"]`
		);
		FormHandler.setErrorField(errorField, errorMessage);
	}

	/**
     * Removes error messages from input fields.
     */
	static removeErrorField(inputField) {
		const sibling = inputField.nextElementSibling;
		if (sibling.classList.contains('error')) {
			sibling.remove();
		}
	}

	/**
     * Sets error messages in Sitecore form error fields.
     */
	static setErrorField(field, message) {
		const errorField = field;
		if (errorField && errorField.classList.contains('field-validation-valid')) {
			errorField.classList.remove('field-validation-valid');
			errorField.classList.add('field-validation-error');
			errorField.innerHTML = message;
		}
	}

	/**
     * Validates a single field on the client side.
     */
	static validate(field) {
		if (!$(field).valid()) {
			FormHandler.handleInvalidField(field);
		} else {
			FormHandler.removeErrorField(field);
			const parent = field.parentElement;
			const errorInputId = field.id;
			const parsedFormId = formIdparser(errorInputId);
			const errorField = parent.querySelector(
				`[data-valmsg-for="${parsedFormId}"]`
			);
			if (errorField.classList.contains('field-validation-error')) {
				field.classList.remove('valid');
				field.classList.add('input-validation-error', 'error');
			} else {
				field.classList.remove('input-validation-error', 'error');
			}
		}
	}
}
